import { Heading, Tag, Text, TextButton, Upsell } from '@zillow/constellation';
import { useEffect, useState } from 'react';

import { DetailedHomeSparkle } from '../HomeSparkle';
import { clickUpsell, createUpsell, viewUpsell } from '../../api/api';
import { TrackingIdType, UpsellDismissedStorageKey, UpsellEntryPointType, UpsellType } from '../../types';
import { CLICK_LEASE_UPSELL, VIEW_LEASE_UPSELL } from '../../utils/ClickstreamEvents';
import type { TrackClickstreamEventFn } from '../../utils/ClickstreamEvents';

type LeaseDashboardUpsellProps = {
  apiUrl: string;
  leaseId: string;
  onCreateUpsell?: (shouldDisplayUpsell: boolean) => void;
  onCloseUpsell?: () => void;
  trackClickstreamEvent: TrackClickstreamEventFn;
};

const TRACKING_ID_TYPE = TrackingIdType.LEASE_ID;
const UPSELL_TYPE = UpsellType.LEASES_PAGE;
const UPSELL_ENTRY_POINT_TYPE = UpsellEntryPointType.LEASES_DASHBOARD;

const LeaseDashboardUpsell = ({
  apiUrl,
  leaseId,
  onCloseUpsell,
  onCreateUpsell,
  trackClickstreamEvent,
}: LeaseDashboardUpsellProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUpsellOpen, setIsUpsellOpen] = useState(true);
  const [upsellInfo, setUpsellInfo] = useState({
    shouldDisplayUpsell: false,
    isInsuranceRequiredForLease: false,
    isInsuranceRequiredForListing: false,
    monthlyPriceEstimate: '',
    redirectUrl: '',
    upsellId: '',
    zip: '',
  });

  useEffect(() => {
    const getUpsellInfo = async () => {
      const data = await createUpsell(UPSELL_TYPE, TRACKING_ID_TYPE, leaseId, apiUrl);
      const { id: upsellId, shouldDisplayUpsell, url: redirectUrl } = data;

      if (shouldDisplayUpsell) {
        setUpsellInfo({
          upsellId,
          redirectUrl,
          ...data,
        });

        onUpsellView(upsellId);
      }

      onCreateUpsell?.(shouldDisplayUpsell);
      setIsLoading(false);
    };

    getUpsellInfo();
  }, [leaseId]);

  const onUpsellView = (upsellId: string) => {
    trackClickstreamEvent(VIEW_LEASE_UPSELL(UPSELL_ENTRY_POINT_TYPE));
    return viewUpsell(upsellId, TRACKING_ID_TYPE, apiUrl);
  };

  const onUpsellClick = () => {
    window.open(upsellInfo.redirectUrl, '_blank');
    trackClickstreamEvent(CLICK_LEASE_UPSELL(UPSELL_ENTRY_POINT_TYPE));
    return clickUpsell(upsellInfo.upsellId, TRACKING_ID_TYPE, apiUrl);
  };

  const onClose = () => {
    localStorage.setItem(`${UpsellDismissedStorageKey.LEASES}${leaseId}`, JSON.stringify(false));
    setIsUpsellOpen(false);
    onCloseUpsell?.();
  };

  const isInsuranceRequired = upsellInfo.isInsuranceRequiredForLease || upsellInfo.isInsuranceRequiredForListing;
  const insuranceRequiredText = upsellInfo.isInsuranceRequiredForLease ? 'Required in lease' : 'Required by landlord';

  if (isLoading || !upsellInfo.shouldDisplayUpsell || !isUpsellOpen) {
    return null;
  }

  return (
    <Upsell
      heading={
        <>
          {isInsuranceRequired && (
            <Tag data-testid="renters-insurance-required-tag" appearance="warning" marginBottom="xs">
              {insuranceRequiredText}
            </Tag>
          )}
          <Heading level={6}>Get renters insurance fast</Heading>
        </>
      }
      body={
        <Text>
          Popular plans near {upsellInfo.zip} start at {upsellInfo.monthlyPriceEstimate}/month
        </Text>
      }
      media={<DetailedHomeSparkle />}
      actionButton={<TextButton onClick={onUpsellClick}>Get started</TextButton>}
      cardElevation={0}
      onClose={onClose}
    />
  );
};

export default LeaseDashboardUpsell;
